import React, { useContext, useEffect } from "react";
import { useTable, useSortBy, useFilters, useRowSelect } from "react-table";

import { useExportData } from "react-table-plugins";
import { FaFilePdf, FaFileExcel } from "react-icons/fa";
import { Grid, Button, Fade, Alert, ButtonGroup } from "@mui/material";
import "../../Scss/stylesComponents.scss?v=1.1.6";

import Papa from "papaparse";
import * as XLSX from "xlsx";
import JsPDF from "jspdf";
import "jspdf-autotable";
import { CustomButtonPlusIcon } from "../Design/CustomButtonPlusIcon";
import { PermissionContext } from "../../Context/Context";
import Swal from "sweetalert2";
import api from "../../../api/axios";
import { Add } from "@mui/icons-material";

const Table = ({
  columns,
  data,
  view,
  viewTitle,
  tableWidth,
  minTableWidth,
  deleteSelected,
  selectedRows,
  setSelectedRows = () => {},
  setChangeDone = () => {},
  changeDone,
  flagToShow,
  icon: Icon,
  viewPermission = "",
  selectionActions,
  apiRoute = "",
  checkBoxFlag = false,
}) => {
  const { can } = useContext(PermissionContext);

  /* ================= EXPORT ================= */

  const deleteSelectedData = async (ids) => {
    const result = await Swal.fire({
      title: "¿Desea eliminar los contactos seleccionados?",
      icon: "warning",
      showDenyButton: true,
      confirmButtonColor: "#1ab394",
      cancelButtonColor: "#d33",
      reverseButtons: true,
      confirmButtonText: "Eliminar",
      denyButtonText: "Volver",
    });

    if (result.isConfirmed) {
      Swal.fire({
        title: "Eliminando...",
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });

      try {
        const response = await api.post(
          process.env.REACT_APP_API_URL + `/${apiRoute}/destroymultiple`,
          { contacts_id: ids },
        );

        Swal.close();
        return { deleted: true, data: response.data };
      } catch (error) {
        Swal.close();
        throw error;
      }
    }

    return { deleted: false };
  };

  const handleDelete = async () => {
    const data = selectedFlatRows.map((row) => row.original.id);

    try {
      const result = await deleteSelectedData(data);

      if (!result.deleted) return; // 🔥 ESTA ES LA CLAVE

      Swal.fire({
        title: "Dato(s) eliminado(s)",
        icon: "success",
        timer: 2000,
        showConfirmButton: false,
      });

      setChangeDone((prev) => !prev);
      setSelectedRows([]);
    } catch (err) {
      Swal.fire({
        title: "Error",
        text: "No se pudieron eliminar los datos.",
        icon: "error",
      });
    }
  };

  const getExportFileBlob = ({ columns, data, fileType, fileName }) => {
    if (fileType === "csv") {
      const headerNames = columns.map((col) => col.exportValue);
      const csvString = Papa.unparse({ fields: headerNames, data });
      return new Blob([csvString], { type: "text/csv" });
    }

    if (fileType === "xlsx") {
      const header = columns.map((c) => c.exportValue);
      const compatibleData = data.map((row) => {
        const obj = {};
        header.forEach((col, index) => {
          obj[col] = row[index];
        });
        return obj;
      });

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(compatibleData, { header });
      XLSX.utils.book_append_sheet(wb, ws, "React Table Data");
      XLSX.writeFile(wb, `${fileName}.xlsx`);
      return false;
    }

    if (fileType === "pdf") {
      const headerNames = columns.map((column) => column.exportValue);
      const doc = new JsPDF();
      doc.autoTable({
        head: [headerNames],
        body: data,
        styles: { fontSize: 6 },
        theme: "grid",
      });
      doc.save(`${fileName}.pdf`);
      return false;
    }

    return false;
  };

  /* ================= TABLE ================= */

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    exportData,
    visibleColumns,
    selectedFlatRows, // filas seleccionadas reales
  } = useTable(
    {
      columns,
      data,
      getExportFileBlob,
    },
    useFilters,
    useSortBy,
    useRowSelect,
    (hooks) => {
      hooks.visibleColumns.push((cols) => {
        // Si flagToShow es false, no incluimos la columna de selección
        if (!checkBoxFlag) return cols;

        return [
          {
            id: "selection",
            disableSortBy: true,
            Header: ({ getToggleAllRowsSelectedProps }) => {
              const { indeterminate, ...rest } =
                getToggleAllRowsSelectedProps();
              return (
                <input
                  type="checkbox"
                  {...rest}
                  ref={(el) => {
                    if (el) el.indeterminate = !!indeterminate;
                  }}
                />
              );
            },
            Cell: ({ row }) => {
              const { indeterminate, ...rest } =
                row.getToggleRowSelectedProps();

              return (
                <input
                  type="checkbox"
                  {...rest}
                  ref={(el) => {
                    if (el) el.indeterminate = !!indeterminate;
                  }}
                />
              );
            },
          },
          ...cols, // el resto de columnas
        ];
      });
    },
  );

  /* ================= COLORS ================= */

  const pastelColors = {
    contactos: { header: "#0d7bc8ff", rowHover: "#f1f8f4" },
    expedientes: { header: "#2e7d32", rowHover: "#2e7d321f" },
    clientes: { header: "rgb(167 103 0)", rowHover: "rgb(255 157 0 / 20%)" },
    default: { header: "rgb(118 118 118)", rowHover: "#f5f5f5" },
  };

  const { header, rowHover } = pastelColors[viewTitle] || pastelColors.default;

  // console.log(data);
  /* ================= RENDER ================= */

  return (
    data && (
      <Fade in timeout={800}>
        <div>
          {/* ACTION BAR */}
          <Grid container item xs={12} sx={{ my: 1, px: "12px" }}>
            <Grid item xs={7}>
              <Fade in={selectedFlatRows.length > 0}>
                <div
                  style={{
                    display: "flex",
                    background: "white",
                    borderRadius: "4px",
                    justifyContent: "space-between",
                  }}>
                  <Button
                    color="error"
                    size="small"
                    variant="contained"
                    onClick={() => {
                      handleDelete();
                    }}>
                    ELIMINAR SELECCIONADOS
                  </Button>
                  {selectionActions}
                </div>
              </Fade>
            </Grid>

            <Grid item xs={5} p={"2px"} sx={{ display: "flex", gap: "8px" }}>
              <ButtonGroup size="small" sx={{ width: "100%" }}>
                <CustomButtonPlusIcon
                  size="small"
                  icon={FaFileExcel}
                  text="CSV"
                  handleFunction={() => exportData("csv", false)}
                  fullWidth
                />

                <CustomButtonPlusIcon
                  size="small"
                  icon={FaFileExcel}
                  text="XLSX"
                  handleFunction={() => exportData("xlsx", false)}
                  fullWidth
                  color="success"
                />

                <CustomButtonPlusIcon
                  size="small"
                  icon={FaFilePdf}
                  text="PDF"
                  handleFunction={() => exportData("pdf", false)}
                  fullWidth
                  color="error"
                />
              </ButtonGroup>
              {Icon &&
                can(viewPermission, "add") &&
                // viewTitle != "tiposdecasos" &&
                flagToShow !== false &&
                viewTitle != "clientes" && (
                  <CustomButtonPlusIcon
                    icon={Icon ? Icon : <Add />}
                    to={`/${viewTitle}/crear`}
                    text={`Crear ${view}`}
                    size="small"
                    variant={"contained"}
                    sxProps={{
                      fontWeight: "700",
                    }}
                    fullWidth
                    color="success"
                  />
                )}
            </Grid>
          </Grid>

          {/* TABLE */}

          <div style={{ overflowX: "auto" }}>
            <table
              {...getTableProps()}
              className="generalTableStyle"
              style={{
                width: tableWidth || "100%",
                minWidth: minTableWidth,
                borderCollapse: "separate",
                borderSpacing: "0 5px",
                fontFamily: "'Roboto', sans-serif",
                fontSize: "14px",
              }}>
              <thead>
                {headerGroups.map((hg) => {
                  const { key: rowKey, ...rowProps } = hg.getHeaderGroupProps();
                  return (
                    <tr key={rowKey} {...rowProps}>
                      {hg.headers.map((column, index) => {
                        const { key: colKey, ...colProps } =
                          column.getHeaderProps();
                        return (
                          <th
                            key={colKey}
                            {...colProps}
                            style={{
                              padding: "10px",
                              background: index === 0 ? "transparent" : "white",
                              color: header,
                              fontWeight: "600",
                              textAlign: "center",
                              width: column.width,
                              minWidth: column.minWidth,
                            }}>
                            {column.render("Header")}
                          </th>
                        );
                      })}
                    </tr>
                  );
                })}
              </thead>

              <tbody {...getTableBodyProps()}>
                {data.length === 0 && (
                  <tr>
                    <td colSpan={visibleColumns.length} style={{ padding: 0 }}>
                      <Alert
                        severity="info"
                        sx={{
                          width: "100%",
                          justifyContent: "center",
                          borderRadius: 0,
                        }}>
                        No se han encontrado resultados...
                      </Alert>
                    </td>
                  </tr>
                )}

                {data.length > 0 &&
                  rows.map((row, idx) => {
                    prepareRow(row);
                    const { key: rowKey, ...rowProps } = row.getRowProps();
                    return (
                      <tr
                        key={rowKey}
                        {...rowProps}
                        style={{
                          backgroundColor: idx % 2 === 0 ? "#f9f9f9" : "#fff",
                          transition: "all 0.2s ease-in-out",
                          cursor: "pointer",
                          boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.backgroundColor = rowHover)
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.backgroundColor =
                            idx % 2 === 0 ? "#f9f9f9" : "#fff")
                        }>
                        {row.cells.map((cell) => {
                          const { key: cellKey, ...cellProps } =
                            cell.getCellProps();
                          return (
                            <td
                              key={cellKey}
                              {...cellProps}
                              style={{
                                borderBottom: "1px solid #ddd",
                                textAlign: "center",
                                width: cell.column.width, // ✅
                                minWidth: cell.column.minWidth, // ✅
                              }}>
                              {cell.render("Cell")}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>
      </Fade>
    )
  );
};

export default Table;
